import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "../../UserProfile.module.css";

import { TextSerif } from "@/components/elements";
import { Modal } from "@/components/elements/Modal";
import { IconEdit, IconPhone } from "@/components/icons";
import { useProfile } from "@/services/profile/get-profile";
import { useUpdateContacts } from "@/services/profile/update-contacts";

import type { FC } from "react";

export const UserProfileTabContacts: FC = () => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure();
  const { data: user } = useProfile();
  const updateContacts = useUpdateContacts();
  const [isChanged, setIsChanged] = useState(false);

  const email = user?.email ?? "";
  const phone_number = user?.additional_info?.phone_number ?? "";

  const form = useForm({
    initialValues: {
      email,
      phone: phone_number,
    },
    validateInputOnChange: false,
    validateInputOnBlur: true,
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t("profile.validation.invalid_email"),
    },
  });

  const handleOpen = () => {
    form.setValues({
      email,
      phone: phone_number,
    });
    open();
  };

  const handleSave = () => {
    const isValid = form.validate();
    if (!isValid.hasErrors) {
      updateContacts.mutate(
        {
          email: form.values.email,
          phone_number: form.values.phone,
        },
        {
          onSuccess: () => {
            setIsChanged(false);
            close();
          },
        }
      );
    }
  };

  const isFormValid = () => {
    const hasErrors = Object.keys(form.errors).length > 0;
    return isChanged && !hasErrors;
  };

  if (!user) return null;

  return (
    <>
      <Box p="md">
        <Group justify="space-between" align="center" h="2.5em">
          <Title order={3} fw="normal" fz="1.25em">
            {t("profile.navLinks.contacts")}
          </Title>
          <ActionIcon radius="sm" color="primary" variant="light" onClick={handleOpen}>
            <IconEdit />
          </ActionIcon>
        </Group>

        <Stack gap="xs" style={{ flex: 1 }}>
          {[
            { label: t("profile.email"), value: email },
            { label: t("profile.phone_number"), value: phone_number },
          ].map((field) => (
            <Box key={field.label + "__" + field.value} py="md">
              <Group justify="space-between" align="center" w="100%">
                <TextSerif size="sm">{field.label}</TextSerif>
                <Text size="md">{field.value}</Text>
              </Group>
              <Box mt="xs" className={classes.userData_list} />
            </Box>
          ))}
        </Stack>
      </Box>

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Flex align="center" gap="md">
            <ThemeIcon radius="100%" color="primary" variant="light">
              <IconPhone />
            </ThemeIcon>
            {t("profile.navLinks.contacts")}
          </Flex>
        }
        centered
      >
        <form onSubmit={form.onSubmit(handleSave)}>
          <Stack gap="xl">
            <Stack gap="xs">
              <TextInput
                label={t("profile.email")}
                placeholder={t("profile.inputFields.enter_email")}
                {...form.getInputProps("email")}
                onChange={(e) => {
                  const newEmail = e.currentTarget.value;
                  form.getInputProps("email").onChange(e);
                  setIsChanged(newEmail !== email || form.values.phone !== phone_number);
                }}
              />

              <TextInput
                label={t("profile.phone_number")}
                placeholder={t("profile.inputFields.enter_phone")}
                {...form.getInputProps("phone")}
                onChange={(e) => {
                  const newPhone = e.currentTarget.value;
                  form.getInputProps("phone").onChange(e);
                  setIsChanged(form.values.email !== email || newPhone !== phone_number);
                }}
              />
            </Stack>

            <Group>
              <Button variant="outline" onClick={close} flex={1} aria-label={t("common.cancel")}>
                {t("common.cancel")}
              </Button>
              <Button
                color="primary"
                type="submit"
                flex={1}
                disabled={!isFormValid() || updateContacts.isPending}
                aria-label={t("common.save")}
              >
                {t("common.save")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
